import FormField from '@ingka/form-field';
import TextArea from '@ingka/text-area';
import DOMPurify from 'dompurify';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shouldValidate } from '../../utils';
import { getLimit } from '../controlled-input-field/util';

export type ControlledTextAreaProps<TFormData extends FieldValues = FieldValues> = {
  labelKey: string;
  control: Control<TFormData>;
  name: Path<TFormData>;
  showCharacterLimit: boolean;
  upperLimit?: number;
  helpText?: string;
};

export const ControlledTextArea = <TFormData extends FieldValues = FieldValues>({
  control,
  labelKey,
  name,
  showCharacterLimit,
  upperLimit,
  helpText,
}: ControlledTextAreaProps<TFormData>) => {
  const { t } = useTranslation();

  const transformErrorMessage = (errorMessage: string | undefined) => {
    return errorMessage ? t(errorMessage, getLimit({ upperLimit, errorMessage })) : '';
  };

  return (
    <Controller
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
        <FormField
          shouldValidate={shouldValidate(error)}
          valid={!error}
          validation={{
            msg: transformErrorMessage(error?.message),
            type: 'error',
          }}
          characterLimit={showCharacterLimit ? upperLimit : undefined}
          fieldHelper={{ msg: helpText }}
        >
          <TextArea
            id={`lottery-form-${name}`}
            label={`${t(labelKey)}`}
            onChange={onChange}
            onBlur={onBlur}
            value={value ? DOMPurify.sanitize(value) : ''}
            data-testid={name.replace(/\./g, '-')}
          />
        </FormField>
      )}
      name={name}
      control={control}
    />
  );
};
